import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

/**
 * Curva-style anchored menu (see `AnchoredMenu` in the editor shell):
 * controlled open, portal panel, Escape + outside pointerdown to close.
 */
export function LabAnchoredMenu({
  open,
  onOpenChange,
  anchorRef,
  children,
  className = "",
  align = "left",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  className?: string;
  align?: "left" | "right";
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | undefined>();

  useLayoutEffect(() => {
    if (!open) {
      setPosition(undefined);
      return;
    }

    function updatePosition(): void {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (!anchor || !menu) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const gap = 4;
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;
      let left = align === "right" ? rect.right - width : rect.left;
      left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));
      const belowTop = rect.bottom + gap;
      const aboveTop = rect.top - height - gap;
      const fitsBelow = belowTop + height <= window.innerHeight - margin;
      const top = fitsBelow ? belowTop : Math.max(margin, aboveTop);
      setPosition({ left, top });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onOpenChange(false);
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange, anchorRef]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className={`lab-anchored-menu fixed z-50 max-h-[min(20rem,calc(100vh-16px))] min-w-44 overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30 ${className}`}
      style={position ? { left: position.left, top: position.top } : { left: 0, top: 0, visibility: "hidden" }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function LabMenuItem({
  label,
  hint,
  title,
  disabled,
  onClick,
}: {
  label: string;
  hint?: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      title={title}
      disabled={disabled}
      className="flex h-8 w-full items-center justify-between gap-3 rounded px-2 text-left text-[12px] text-slate-300 hover:bg-white/[0.08] disabled:pointer-events-none disabled:opacity-40"
      onClick={onClick}
    >
      <span className="truncate">{label}</span>
      {hint ? <span className="shrink-0 text-[11px] text-slate-500">{hint}</span> : null}
    </button>
  );
}

/** Curva TopBar MenuToggle: Check column, not menuitemcheckbox. */
export function LabMenuToggle({
  label,
  checked,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className="flex h-8 w-full items-center gap-2 rounded px-2 text-left text-[12px] text-slate-300 hover:bg-white/[0.08] disabled:pointer-events-none disabled:opacity-40"
      onClick={onClick}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {checked ? <Check className="size-3.5" aria-hidden /> : null}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

export function LabMenuSeparator() {
  return (
    <div
      role="separator"
      className="my-1 h-px bg-white/[0.08]"
      aria-hidden={true}
    />
  );
}

export function LabMenuRadio({
  label,
  checked,
  onClick,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="flex h-8 w-full items-center gap-2 rounded px-2 text-left text-[12px] text-slate-300 hover:bg-white/[0.08]"
      onClick={onClick}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {checked ? <Check className="size-3.5" aria-hidden /> : null}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

export function LabToolbarMenu({
  label,
  title,
  open,
  onOpenChange,
  align = "left",
  disabled,
  children,
}: {
  label: string;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: "left" | "right";
  disabled?: boolean;
  children: ReactNode;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        className="rounded border border-lab-line bg-lab-bg px-[0.55rem] py-[0.35rem] text-[11px] tracking-[0.02em] text-lab-text hover:bg-white/[0.06] disabled:cursor-default disabled:opacity-45"
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled ? "true" : undefined}
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return;
          }
          onOpenChange(!open);
        }}
      >
        {label}
      </button>
      <LabAnchoredMenu
        open={open && !disabled}
        onOpenChange={onOpenChange}
        anchorRef={anchorRef}
        align={align}
      >
        {children}
      </LabAnchoredMenu>
    </div>
  );
}
